/**
 * Report · packages and OCRs — V98-5-4 / D-98-10 (ProgramShell Report tab).
 *
 * Each draft series shows ONE prerequisites list — the same rows feed the
 * state chip ("Blocked · N"), the Freeze button, the calendar and the
 * approver's card. The lifecycle comes from the server
 * (draft → frozen → signed; obsolete when a blocker opens):
 *
 *   blocked   the open blocking prerequisites, each with its owner, due date
 *             and the action that clears it (Mark done… for its owner / the
 *             lead; derived rows open the record that clears them; "No
 *             longer applies…" for the lead) + "Check again" (V98-5 review
 *             T5-B1: every read re-checks derived causes)
 *   ready     "Freeze vN → send to <approver>" (the program lead / an admin)
 *   frozen    "vN · with <approver>" + Open vN + Withdraw vN…; changes that
 *             don't block are notes for the next version (T5-H9)
 *   replaced  "Earlier version replaced · <why>" above the next freeze
 *
 * Tones (T5-H5): red = blocked / blocks signing; neutral = with the approver.
 * Focus (T5-H15): after Mark done / Freeze / Withdraw, the next row or the
 * list heading; inline forms focus their first field and return to the
 * trigger (kept mounted, aria-expanded).
 *
 * Reads: GET /package-drafts?profile_id= (any member). Writes: lead/admin
 * (create, add, freeze, withdraw a prerequisite or a frozen version), the
 * prerequisite owner (mark done).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Field, Input, Select, Spinner, Textarea } from '@aigovops/design-system';
import type { PackagePrerequisite, PackageView } from '@aigovops/core';
import {
  addPackagePrerequisite, clearPackagePrerequisite, createPackageDraft, freezePackageDraft, listPackageDrafts, syncPackageDraft, withdrawSignature,
} from '../../lib/cert-ops-api';
import { ApiError } from '../../lib/api';
import { fmtDate, prereqTask } from '../../lib/cert-ops-model';
import { firstName, nameOf, type Names } from '../../lib/today-model';
import { ActionDialog, Chip, domId, errorText, useAnnounce, useFocusAfter } from '../today/shared';
import '../../pages/cert-ops.css';

export function ReportPackages({ slug, prefix, profileId, me, canManage, names }: {
  slug: string; prefix: string; profileId: string; me: string; canManage: boolean; names: Names;
}) {
  const q = useQuery({ queryKey: ['package-drafts', slug, profileId], queryFn: () => listPackageDrafts(slug, profileId), retry: false });
  const [announceNode, announce] = useAnnounce();
  const focusLater = useFocusAfter();
  if (q.isLoading) return <Spinner label="Loading reports" />;
  if (q.error || !q.data) return <Alert variant="error"><p>Reports could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const onDone = (m: string, focusId: string) => { focusLater(focusId); announce(m); };
  return (
    <section className="gp-sec" aria-labelledby="pkg-h" data-part="report-packages">
      {announceNode}
      <h3 id="pkg-h" className="gp-sec-h" tabIndex={-1}>Reports to sign</h3>
      {q.data.drafts.length === 0
        ? (canManage ? <NewDraftForm slug={slug} profileId={profileId} onDone={(m) => onDone(m, 'pkg-h')} /> : <p className="gp-dim">No report drafted yet · the program lead drafts the next one.</p>)
        : q.data.drafts.map((v) => <DraftCard key={v.draft.draft_id} slug={slug} prefix={prefix} v={v} me={me} canManage={canManage} names={names} onDone={onDone} />)}
    </section>
  );
}

/** The draft's state, one tone per meaning (T5-H5) — the calendar's "Next up" chip reuses it. */
export function draftState(v: PackageView, names: Names): { text: string; tone: 'red' | 'neutral' | 'ok' } {
  const lc = v.lifecycle;
  const approver = lc.approver_user_id ?? v.freeze.approver_user_id;
  const approverName = approver ? firstName(nameOf(names, approver)) : 'the approver';
  if (v.blocking > 0) return { text: `Blocked · ${v.blocking}`, tone: 'red' };
  if (lc.state === 'frozen') return { text: `v${lc.version} · with ${approverName}`, tone: 'neutral' };
  if (lc.state === 'signed') return { text: `v${lc.version} signed by ${firstName(nameOf(names, lc.signed_by_user_id))}`, tone: 'ok' };
  if (lc.state === 'released') return { text: `v${lc.version} released`, tone: 'ok' };
  if (lc.state === 'returned') return { text: 'Sent back', tone: 'red' };
  if (lc.obsolete.length) return { text: 'Earlier version replaced', tone: 'neutral' };
  return { text: `Ready for ${approverName}`, tone: 'ok' };
}

function DraftCard({ slug, prefix, v, me, canManage, names, onDone }: {
  slug: string; prefix: string; v: PackageView; me: string; canManage: boolean; names: Names; onDone: (m: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['package-drafts', slug] }), qc.invalidateQueries({ queryKey: ['today', slug] }), qc.invalidateQueries({ queryKey: ['calendar', slug] })]);
  const d = v.draft;
  const lc = v.lifecycle;
  const approver = lc.approver_user_id ?? v.freeze.approver_user_id;
  const approverName = approver ? firstName(nameOf(names, approver)) : 'the approver';
  const state = draftState(v, names);
  const listId = domId('pkg-list', d.draft_id);
  const freeze = async () => {
    setBusy(true); setErr(null);
    try { const r = await freezePackageDraft(slug, d.draft_id); await refresh(); onDone(`v${r.request.version} frozen · sent to ${approverName}`, domId('pkg-card', d.draft_id)); }
    catch (e) { setErr(errorText(e, 'It could not be frozen.')); await refresh(); }
    finally { setBusy(false); }
  };
  // T5-B1: reads re-check the derived causes; the lead's "Check again" also re-syncs explicitly.
  const checkAgain = async () => {
    setBusy(true); setErr(null);
    try {
      if (canManage) await syncPackageDraft(slug, d.draft_id);
      await refresh();
      onDone('Checked again', listId);
    } catch (e) { setErr(errorText(e, 'It could not be checked.')); }
    finally { setBusy(false); }
  };
  const open = v.prerequisites.filter((p) => p.state === 'open');
  const done = v.prerequisites.filter((p) => p.state !== 'open');
  const nextFocus = (i: number) => (open[i + 1] ? domId('pkg-pr', open[i + 1]!.prerequisite_id) : open[i - 1] ? domId('pkg-pr', open[i - 1]!.prerequisite_id) : listId);
  return (
    <Card>
      <div className="gp-form" data-draft={d.draft_id} id={domId('pkg-card', d.draft_id)} tabIndex={-1}>
        <div className="gp-ow-line">
          <strong>{d.title}</strong>
          <Chip text={state.text} tone={state.tone} />
        </div>
        <p className="gp-dim">
          {[d.sign_by ? `Sign by ${fmtDate(d.sign_by)}` : null, d.filing_due ? `filing due ${fmtDate(d.filing_due)}` : null, approver ? `${nameOf(names, approver)} signs` : 'No approver assigned yet'].filter(Boolean).join(' · ')}
        </p>
        {lc.obsolete.length && lc.state !== 'frozen' && lc.state !== 'signed' && lc.state !== 'released' ? (
          <p className="gp-dim" data-part="obsolete">{`Earlier version replaced · ${(lc.obsolete[0]!.reason ?? 'a prerequisite changed').replace(/^A prerequisite opened: /, 'a prerequisite opened: ')}`}</p>
        ) : null}
        {lc.state === 'returned' && lc.returned_reason ? <p className="gp-state gp-state--warn">{`Sent back · ${lc.returned_reason}`}</p> : null}
        {err ? <Alert variant="error">{err}</Alert> : null}

        <ol className="gp-owed-list" aria-label={`Prerequisites · ${d.title}`} id={listId} tabIndex={-1}>
          {open.map((p, i) => <PrereqRow key={p.prerequisite_id} n={i + 1} p={p} slug={slug} prefix={prefix} me={me} canManage={canManage} names={names}
            onDone={async (m) => { await refresh(); onDone(m, nextFocus(i)); }} />)}
          {done.map((p) => (
            <li key={p.prerequisite_id} className="gp-ow gp-ow--wait">
              <span className="gp-ow-main">
                <span className="gp-ow-t">{p.title}</span>
                <span className="gp-ow-meta"><Chip text={p.state === 'withdrawn' ? 'No longer applies' : 'Done'} tone="ok" />
                  <span>{`${fmtDate(p.cleared_at)}${p.cleared_by && !p.cleared_by.startsWith('system:') ? ` · ${nameOf(names, p.cleared_by)}` : ''}${p.clear_note && !p.cleared_by?.startsWith('system:') ? ` · ${p.clear_note}` : ''}`}</span></span>
              </span>
            </li>
          ))}
        </ol>
        {v.notes_since_freeze.length && (lc.state === 'frozen' || lc.state === 'signed') ? (
          <div data-part="freeze-notes">
            <p className="gp-dim">{`Changed since v${lc.version} froze · noted for the next version`}</p>
            <ul className="gp-plain-list gp-dim">{v.notes_since_freeze.map((n) => <li key={n}>{n}</li>)}</ul>
          </div>
        ) : null}

        <div className="gp-actions">
          {canManage && v.freeze.allowed
            ? <Button variant="primary" aria-disabled={busy || undefined} onClick={() => void freeze()}>
              {busy ? 'Freezing…' : <>{`Freeze v${v.next_version} `}<span aria-hidden="true">→</span><span className="gp-sr-only">and</span>{` send to ${approverName}`}</>}
            </Button>
            : null}
          {v.blocking > 0 ? <Button variant="secondary" aria-disabled={busy || undefined} onClick={() => void checkAgain()}>Check again</Button> : null}
          {lc.request_id && (lc.state === 'frozen' || lc.state === 'signed' || lc.state === 'released' || lc.state === 'returned')
            ? <Link className="gp-linkbtn" to={`${prefix}/items/signature_request/${encodeURIComponent(lc.request_id)}?from=program:fedramp-cr26`}>{`Open v${lc.version}`}</Link> : null}
          {canManage && lc.state === 'frozen' && lc.request_id ? <Button variant="ghost" onClick={() => setWithdrawing(true)}>{`Withdraw v${lc.version}…`}</Button> : null}
          {canManage ? <Button variant="ghost" ref={addRef} aria-expanded={adding} aria-controls={domId('pkg-add', d.draft_id)} onClick={() => setAdding((x) => !x)}>Add a prerequisite</Button> : null}
          {!canManage && v.freeze.allowed ? <span className="gp-dim">Ready · the program lead freezes and sends it</span> : null}
        </div>
        <div id={domId('pkg-add', d.draft_id)} hidden={!adding}>
          {adding ? <AddPrereqForm slug={slug} draftId={d.draft_id} frozen={lc.state === 'frozen' ? { version: lc.version ?? 0, approver: approverName } : null} names={names}
            onCancel={() => { setAdding(false); addRef.current?.focus(); }}
            onDone={async (m) => { setAdding(false); await refresh(); addRef.current?.focus(); onDone(m, listId); }} /> : null}
        </div>
      </div>
      {lc.request_id ? <WithdrawDialog open={withdrawing} slug={slug} requestId={lc.request_id} version={lc.version ?? 0} onClose={() => setWithdrawing(false)}
        onDone={async (m) => { setWithdrawing(false); await refresh(); onDone(m, domId('pkg-card', d.draft_id)); }} /> : null}
    </Card>
  );
}

function PrereqRow({ n, p, slug, prefix, me, canManage, names, onDone }: {
  n: number; p: PackagePrerequisite; slug: string; prefix: string; me: string; canManage: boolean; names: Names; onDone: (m: string) => Promise<void>;
}) {
  const [dialog, setDialog] = useState<'done' | 'withdraw' | null>(null);
  const canMarkDone = !p.derived && (canManage || p.owner_user_id === me);
  const link = p.ref_kind === 'scn' && p.ref_id ? { to: `${prefix}/items/scn/${encodeURIComponent(p.ref_id)}?from=program:fedramp-cr26`, label: 'Send change notice' }
    : p.ref_kind === 'acceptance' && p.ref_id ? { to: `${prefix}/exceptions?acceptance=${encodeURIComponent(p.ref_id)}`, label: 'Renew' }
      : p.ref_kind === 'cds_grant' && p.ref_id ? { to: `${prefix}/calendar#grant-${encodeURIComponent(p.ref_id)}`, label: 'Confirm usage' } : null;
  return (
    <li className="gp-ow" data-prereq={p.kind} id={domId('pkg-pr', p.prerequisite_id)} tabIndex={-1}>
      <span className="gp-ow-main">
        <span className="gp-ow-t">{`${n}. ${prereqTask(p)}`}</span>
        <span className="gp-ow-meta">
          <Chip text={p.blocks_signing ? 'Blocks signing' : 'Won’t block'} tone={p.blocks_signing ? 'red' : 'neutral'} />
          <span>{[p.owner_user_id ? nameOf(names, p.owner_user_id) : 'Unassigned', p.due_at ? `due ${fmtDate(p.due_at)}` : null].filter(Boolean).join(' · ')}</span>
        </span>
      </span>
      <span className="gp-ow-act">
        {link ? <Link className="gp-linkbtn gp-linkbtn--compact" to={link.to} aria-label={`${link.label} · ${p.title}`}>{link.label}</Link> : null}
        {canMarkDone ? <Button compact onClick={() => setDialog('done')} aria-label={`Mark done · ${p.title}`}>Mark done…</Button> : null}
        {canManage ? <Button compact variant="ghost" onClick={() => setDialog('withdraw')} aria-label={`No longer applies · ${p.title}`}>No longer applies…</Button> : null}
        {!link && !canMarkDone && !canManage ? <span className="gp-dim">{`${p.owner_user_id ? firstName(nameOf(names, p.owner_user_id)) : 'The lead'} clears it`}</span> : null}
      </span>
      <ClearDialog open={dialog !== null} withdraw={dialog === 'withdraw'} slug={slug} p={p} onClose={() => setDialog(null)}
        onDone={async (m) => { setDialog(null); await onDone(m); }} />
    </li>
  );
}

/** "Mark done…" (optional note) / "No longer applies…" (lead, reason required). */
export function ClearDialog({ open, withdraw, slug, p, onClose, onDone }: {
  open: boolean; withdraw: boolean; slug: string; p: PackagePrerequisite; onClose: () => void; onDone: (m: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [errs, setErrs] = useState<{ note?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={open} title={`${withdraw ? 'No longer applies' : 'Mark done'} · ${p.title}`} submitLabel={withdraw ? 'Record' : 'Mark done'} busy={busy} error={err}
      onClose={() => { setNote(''); setErrs({}); setErr(null); onClose(); }}
      onSubmit={async () => {
        if (withdraw && note.trim().length < 3) { setErrs({ note: 'Say why it no longer applies.' }); document.getElementById('pr-clear-note')?.focus(); return false; }
        setBusy(true); setErr(null);
        try {
          await clearPackagePrerequisite(slug, p.prerequisite_id, { ...(note.trim() ? { note: note.trim() } : {}), ...(withdraw ? { withdraw: true } : {}) });
          setNote('');
          await onDone(withdraw ? `No longer applies · ${p.title}` : `Done · ${p.title}`);
          return true;
        } catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id="pr-clear-note" label={withdraw ? 'Why it no longer applies' : 'Note'} required={withdraw} hint={withdraw ? undefined : 'Optional'} error={errs.note}>
        <Textarea rows={3} maxLength={2000} value={note} onChange={(e) => { setNote(e.currentTarget.value); setErrs({}); }} />
      </Field>
    </ActionDialog>
  );
}

function WithdrawDialog({ open, slug, requestId, version, onClose, onDone }: {
  open: boolean; slug: string; requestId: string; version: number; onClose: () => void; onDone: (m: string) => Promise<void>;
}) {
  const [why, setWhy] = useState('');
  const [errs, setErrs] = useState<{ why?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={open} title={`Withdraw v${version}`} submitLabel={`Withdraw v${version}`} danger busy={busy} error={err}
      onClose={() => { setWhy(''); setErrs({}); setErr(null); onClose(); }}
      onSubmit={async () => {
        if (why.trim().length < 3) { setErrs({ why: 'Say why it is withdrawn.' }); document.getElementById('pkg-withdraw-why')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await withdrawSignature(slug, requestId, why.trim()); setWhy(''); await onDone(`v${version} withdrawn`); return true; }
        catch (x) { setErr(errorText(x, 'It could not be withdrawn.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">The approver can no longer sign it. Freeze a new version when it’s ready.</p>
      <Field id="pkg-withdraw-why" label="Why" required error={errs.why}>
        <Textarea rows={2} maxLength={2000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setErrs({}); }} />
      </Field>
    </ActionDialog>
  );
}

function AddPrereqForm({ slug, draftId, frozen, names, onCancel, onDone }: {
  slug: string; draftId: string; frozen: { version: number; approver: string } | null; names: Names; onCancel: () => void; onDone: (m: string) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [blocks, setBlocks] = useState(true);
  const [errs, setErrs] = useState<{ title?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const add = async (confirmObsolete: boolean) => {
    setErr(null);
    try {
      await addPackagePrerequisite(slug, draftId, { title: title.trim(), owner_user_id: owner || null, due_at: due ? `${due}T23:59:59.000Z` : null, blocks_signing: blocks, ...(confirmObsolete ? { confirm_obsolete: true } : {}) });
      await onDone(`Added · ${title.trim()}`);
      return true;
    } catch (x) {
      // T5-H9: a blocker while a version is frozen makes it obsolete — confirm first.
      if (x instanceof ApiError && x.status === 409 && (x.body as { code?: string } | undefined)?.code === 'would_obsolete') { setConfirm(true); return false; }
      setErr(errorText(x, 'It could not be added.'));
      return false;
    }
  };
  return (
    <form className="gp-form gp-form--narrow" noValidate aria-label="Add a prerequisite" onSubmit={async (e) => {
      e.preventDefault();
      if (title.trim().length < 3) { setErrs({ title: 'Name what must be done first.' }); first.current?.focus(); return; }
      await add(false);
    }}>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <Field id={`pr-title-${draftId}`} label="What must be done first" required error={errs.title}>
        <Input ref={first} value={title} maxLength={300} onChange={(e) => { setTitle(e.currentTarget.value); setErrs({}); }} />
      </Field>
      <div className="gp-form-row">
        <Field id={`pr-owner-${draftId}`} label="Owner">
          <Select value={owner} onChange={(e) => setOwner(e.currentTarget.value)}>
            <option value="">Unassigned</option>
            {[...names.entries()].map(([u, n]) => <option key={u} value={u}>{n}</option>)}
          </Select>
        </Field>
        <Field id={`pr-due-${draftId}`} label="Due">
          <Input type="date" value={due} onChange={(e) => setDue(e.currentTarget.value)} />
        </Field>
        <Field id={`pr-blocks-${draftId}`} label="Blocks signing?">
          <Select value={blocks ? 'yes' : 'no'} onChange={(e) => setBlocks(e.currentTarget.value === 'yes')}>
            <option value="yes">Yes</option><option value="no">No · shows as pending</option>
          </Select>
        </Field>
      </div>
      <div className="gp-actions">
        <Button type="submit" variant="secondary">Add prerequisite</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
      <ActionDialog open={confirm} title={`Replace v${frozen?.version ?? ''}?`} submitLabel={`Add and replace v${frozen?.version ?? ''}`} danger
        onClose={() => setConfirm(false)} onSubmit={async () => { const ok = await add(true); if (ok) setConfirm(false); return ok; }}>
        <p>{`v${frozen?.version ?? ''} is with ${frozen?.approver ?? 'the approver'}. A prerequisite that blocks signing replaces it: ${frozen?.approver ?? 'the approver'} can’t sign it, and the next version freezes once this is done.`}</p>
      </ActionDialog>
    </form>
  );
}

function NewDraftForm({ slug, profileId, onDone }: { slug: string; profileId: string; onDone: (m: string) => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState('ongoing_certification_report');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [signBy, setSignBy] = useState('');
  const [filing, setFiling] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const ocr = kind === 'ongoing_certification_report';
  return (
    <Card>
      <form className="gp-inline-form gp-form--narrow" noValidate aria-labelledby="new-draft-h" onSubmit={async (e) => {
        e.preventDefault();
        const x: Record<string, string> = {};
        if (ocr && !start) x.start = 'Choose the first day of the report period.';
        if (ocr && !end) x.end = 'Choose the last day.';
        else if (ocr && end < start) x.end = 'Ends on or after the start.';
        if (signBy && filing && signBy > filing) x.sign = 'Sign on or before the filing date.';
        setErrs(x);
        const firstBad = x.start ? 'nd-start' : x.end ? 'nd-end' : x.sign ? 'nd-sign' : null;
        if (firstBad) { document.getElementById(firstBad)?.focus(); return; }
        setErr(null);
        try {
          await createPackageDraft(slug, { profile_id: profileId, kind, ...(ocr ? { period_start: start, period_end: end } : {}), ...(signBy ? { sign_by: signBy } : {}), ...(filing ? { filing_due: filing } : {}) });
          await qc.invalidateQueries({ queryKey: ['package-drafts', slug] });
          onDone('Draft created');
        } catch (y) { setErr(errorText(y, 'The draft could not be created.')); }
      }}>
        <h3 id="new-draft-h">Draft the next report</h3>
        {err ? <Alert variant="error">{err}</Alert> : null}
        <Field id="nd-kind" label="Report">
          <Select value={kind} onChange={(e) => setKind(e.currentTarget.value)}>
            <option value="ongoing_certification_report">Ongoing certification report (quarterly)</option>
            <option value="security_decision_record">Security decision record</option>
            <option value="certification_package_overview">Certification package overview</option>
          </Select>
        </Field>
        {ocr ? (
          <div className="gp-form-row">
            <Field id="nd-start" label="Period start" required error={errs.start}><Input type="date" value={start} onChange={(e) => setStart(e.currentTarget.value)} /></Field>
            <Field id="nd-end" label="Period end" required error={errs.end}><Input type="date" value={end} onChange={(e) => setEnd(e.currentTarget.value)} /></Field>
          </div>
        ) : null}
        <div className="gp-form-row">
          <Field id="nd-sign" label="Sign by" error={errs.sign}><Input type="date" value={signBy} onChange={(e) => setSignBy(e.currentTarget.value)} /></Field>
          <Field id="nd-filing" label="Filing due"><Input type="date" value={filing} onChange={(e) => setFiling(e.currentTarget.value)} /></Field>
        </div>
        <div className="gp-actions"><Button type="submit" variant="primary">Create draft</Button></div>
      </form>
    </Card>
  );
}
